import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, FileText, Zap, SlidersHorizontal } from "lucide-react";
import { ENGAGEMENTS } from "@/lib/content";
import { Eyebrow, MaskLines, Reveal } from "@/components/site/Motion";
import { EASE_OUT } from "@/lib/scroll";

const ICONS = [ShieldCheck, FileText, Zap, SlidersHorizontal];

export default function Engagements() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const line = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="engagements" className="relative py-24 sm:py-36 bg-[#0c1222]/50 border-y border-white/5" data-testid="engagements-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-[#0af5f0]/10 blur-[160px]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Eyebrow index="02">Nos engagements</Eyebrow>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              <MaskLines inView lines={["Un standard", <span key="b" className="font-serif-i text-brand">qui ne se négocie pas.</span>]} />
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md text-base sm:text-lg text-slate-300 leading-relaxed">
                La propreté se juge dans le détail. Voici les quatre principes qui guident chacune de nos interventions.
              </p>
            </Reveal>
          </div>
        </div>

        <div ref={ref} className="lg:col-span-7 relative pl-8 sm:pl-12">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-white/10" />
          <motion.div style={{ height: line }} className="absolute left-0 top-0 w-px bg-gradient-to-b from-[#5aec5a] via-[#0af5f0] to-[#22a6ff] shadow-[0_0_12px_#0af5f0]" />
          {ENGAGEMENTS.map((e, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={e.title}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.9, ease: EASE_OUT }}
                className="group relative py-10 sm:py-12 border-b border-white/10 last:border-0"
                data-testid={`engagement-item-${i}`}
              >
                <span className="absolute -left-[37px] sm:-left-[53px] top-12 sm:top-14 h-2.5 w-2.5 rounded-full bg-[#050811] border border-cyan-300 group-hover:bg-cyan-300 transition-colors duration-300" />
                <div className="flex items-start gap-6">
                  <span className="font-mono-uc text-sm text-slate-400 pt-3">0{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <h3 className="font-display text-3xl sm:text-5xl font-semibold text-white transition-transform duration-500 group-hover:translate-x-2">
                        {e.title}
                      </h3>
                      <Icon className="h-6 w-6 text-cyan-300 opacity-60 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />
                    </div>
                    <p className="mt-4 max-w-lg text-base text-slate-300 leading-relaxed">{e.text}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
